import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useLang } from '@/context/LanguageContext';
import { EDICIONS } from '@/data/catalog';
import { Stars } from '@/components/Stars';
import { ArrowUpRight, ArrowRight } from 'lucide-react';

// Rejilla de ediciones anteriores.
//
// No se escribe aquí: cada cuadro sale del bloque "edicions" de
// festivalData.json, con el año, el destino y los tres colores de la campaña de
// ese año. Si la lista está vacía, la sección no se pinta y la web queda
// exactamente como estaba.
//
// Una edición con "url" lleva al archivo en WordPress, fuera del sitio; una con
// "ruta" lleva a una página de esta misma web, como la del programa de 2026, y
// se marca con un filete de su color para que se vea que no sale fuera.
//
// Cada edición se distingue por el color, no por una imagen: así la rejilla no
// depende de que el WordPress del archivo siga en pie, y añadir un año es una
// línea en el JSON, sin subir nada.

const reveal = {
  hidden: { opacity: 0, y: 18 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.07, ease: 'easeOut' },
  }),
};

const CARD_CLASS =
  'group relative block aspect-[4/5] overflow-hidden rounded-2xl focus:outline-none focus-visible:ring-2 focus-visible:ring-[var(--cea-accent)] focus-visible:ring-offset-2 focus-visible:ring-offset-[#040f0c] transition-transform duration-300 ease-out hover:-translate-y-1.5';

export const Edicions = () => {
  const { t } = useLang();

  if (EDICIONS.length === 0) return null;

  const Inner = ({ e }) => (
    <>
      <span className="absolute left-4 top-4 inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-[0.14em] opacity-70">
        {e.ruta ? t.edicions.program : t.edicions.archive}
        {e.ruta ? <ArrowRight size={12} /> : <ArrowUpRight size={12} />}
      </span>

      <span
        aria-hidden="true"
        style={{ borderColor: e.marca }}
        className="absolute right-4 top-4 block h-11 w-11 rounded-full border-[7px] transition-transform duration-500 ease-out group-hover:scale-125 group-hover:rotate-[24deg]"
      />

      <span className="absolute inset-x-4 bottom-1 block font-display font-normal uppercase leading-[0.78] tracking-wide text-5xl sm:text-6xl">
        {e.any}
      </span>
    </>
  );

  return (
    <section
      id="edicions"
      data-testid="edicions-section"
      className="relative overflow-hidden scroll-mt-20 py-20 sm:py-28 bg-[#040f0c]"
    >
      <Stars count={26} seed={11} />

      <div className="relative z-10 max-w-6xl mx-auto px-4 sm:px-6">
        <div className="max-w-2xl">
          <span className="text-xs font-bold tracking-[0.18em] text-[var(--cea-accent)] uppercase">
            {t.edicions.tag}
          </span>
          <h2 className="mt-3 font-display font-medium text-3xl sm:text-4xl lg:text-5xl tracking-[-0.01em]">
            {t.edicions.title}
          </h2>
          <p className="mt-4 text-white/70">{t.edicions.subtitle}</p>
        </div>

        <div
          className="mt-10 grid grid-cols-2 gap-4 sm:gap-5 md:grid-cols-3 lg:grid-cols-5"
          data-testid="edicions-grid"
        >
          {EDICIONS.map((e, i) => {
            const style = {
              backgroundColor: e.fons,
              color: e.tinta,
              // El cuadro que no sale del sitio lleva filete: sobre el fondo
              // oscuro de la sección, si no, no se vería dónde acaba.
              ...(e.ruta ? { boxShadow: `inset 0 0 0 2px ${e.marca}` } : null),
            };

            return (
              <motion.div
                key={e.any}
                custom={i}
                variants={reveal}
                initial="hidden"
                whileInView="show"
                viewport={{ once: true, amount: 0.3 }}
              >
                {e.ruta ? (
                  <Link
                    to={e.ruta}
                    data-testid={`edicio-${e.any}`}
                    aria-label={`${t.edicions.tag} ${e.any}`}
                    style={style}
                    className={CARD_CLASS}
                  >
                    <Inner e={e} />
                  </Link>
                ) : (
                  <a
                    href={e.url}
                    target="_blank"
                    rel="noreferrer"
                    data-testid={`edicio-${e.any}`}
                    aria-label={`${t.edicions.tag} ${e.any}`}
                    style={style}
                    className={CARD_CLASS}
                  >
                    <Inner e={e} />
                  </a>
                )}
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
